// src/propertyManagement/pages/PMWorkOrderDetail.jsx
import React, { useEffect, useState, useRef } from 'react';
import {
  Add,
  ChatBubbleOutline,
  SwapHoriz,
  Print,
  PictureAsPdf
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import logoPng from '../../assets/901homesolutions.png';
import {
  doc,
  getDoc,
  updateDoc,
  getDocs,
  collection,
  query,
  where,
  onSnapshot,
  addDoc,
  serverTimestamp,
  arrayUnion
} from 'firebase/firestore';
import { db, storage } from '../../firebase';
import {
  Box,
  Typography,
  Paper,
  Divider,
  Avatar,
  Select,
  MenuItem,
  Dialog,
  FormControl,
  InputLabel,
  Button,
  Grid,
  TextField,
  List,
  ListItem,
  ListItemText,
  ToggleButton,
  ToggleButtonGroup,
  Modal,
  IconButton,
  OutlinedInput,
  Checkbox,
  ListItemIcon
} from '@mui/material';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';

export default function PMWorkOrderDetail() {
  const { id } = useParams();
  const [workOrder, setWorkOrder] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [attachments, setAttachments] = useState([]); // combined images + files
  const [expandedUrl, setExpandedUrl] = useState(null);
const [vaUsers, setVaUsers] = useState([]);
const [assignedVa, setAssignedVa] = useState('');
const [prevAssignedVa, setPrevAssignedVa] = useState('');
const [vaInfo, setVaInfo] = useState(null);
  const [status, setStatus] = useState('');
  const [prevStatus, setPrevStatus] = useState('');
  const [priority, setPriority] = useState('');
  const [prevPriority, setPrevPriority] = useState('');
  const [assignedVendor, setAssignedVendor] = useState('');
  const [vendorInfo, setVendorInfo] = useState(null);
  const [assigneeType, setAssigneeType] = useState('staff');
  const [collaborators, setCollaborators] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [expandedImage, setExpandedImage] = useState(null);
  const [prevAssignedVendor, setPrevAssignedVendor] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const fileInputRef = useRef();

  // 1) Fetch initial workOrder data & vendor list & all users
  useEffect(() => {
    const fetchData = async () => {
      const woSnap = await getDoc(doc(db, 'workOrders', id));
      if (!woSnap.exists()) return;
      const data = woSnap.data();
      setWorkOrder({ id: woSnap.id, ...data });
      setStatus(data.status || '');
      setPrevStatus(data.status || '');
      setPriority(data.priority || '');
      setPrevPriority(data.priority || '');
      setAssignedVendor(data.assignedVendorId || '');
      setPrevAssignedVendor(data.assignedVendorId || '');
      setAssignedVa(data.assignedVaId || '');
setPrevAssignedVa(data.assignedVaId || '');
    };
    const fetchVendors = async () => {
      const q = query(collection(db, 'users'), where('role', '==', 'vendor'));
      const vs = await getDocs(q);
      setVendors(vs.docs.map(d => ({ id: d.id, ...d.data() })));
    };
          const fetchVAs = async () => {
        const q = query(collection(db, 'users'), where('role', '==', 'VA'));
        const ps = await getDocs(q);
        setVaUsers(ps.docs.map(d => ({ id: d.id, ...d.data() })));
      };
      fetchVAs();
    const fetchAllUsers = async () => {
      const q = query(collection(db, 'users'));
      const us = await getDocs(q);
      setAllUsers(us.docs.map(d => ({ id: d.id, ...d.data() })));
    };

    fetchData();
    fetchVendors();
    fetchAllUsers();
  }, [id]);

  // 2) Listen for changes to the workOrder doc (to pick up new single-image fields)
  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'workOrders', id), snap => {
      if (!snap.exists()) return;
      const data = snap.data();

      // start with any single image fields
      const imgs = [];
      if (data.ppUrl)    imgs.push({ url: data.ppUrl, name: 'Tenant Upload', type: 'image' });
      if (data.imageUrl) imgs.push({ url: data.imageUrl, name: 'Image', type: 'image' });

      // then any array images
      if (Array.isArray(data.imageUrls)) {
        data.imageUrls.forEach(f => imgs.push({ url: f.url, name: f.name, type: 'image' }));
      }

      // merge with existing file attachments but preserve order
      setAttachments(prev => {
        // keep only the images here; we'll add files in the next listener
        const onlyFiles = prev.filter(x => x.type === 'file');
        return [...imgs, ...onlyFiles];
      });

      setWorkOrder(prev => ({ ...prev, ...data }));
    });
    return () => unsub();
  }, [id]);

  // 3) Listen for PDF & other files in subcollection workOrders/{id}/files
  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'workOrders', id, 'files'),
      snap => {
        const files = snap.docs.map(d => {
          const ddata = d.data();
          return {
            url: ddata.url,
            name: ddata.name,
            type: 'file',      // signal “PDF or other”
            fileType: ddata.type // e.g. 'receipt'
          };
        });
        setAttachments(prev => {
          // keep only image entries; then append updated files
          const onlyImages = prev.filter(x => x.type === 'image');
          return [...onlyImages, ...files];
        });
      }
    );
    return () => unsub();
  }, [id]);

  // 4) Updates sub‐collection listener
  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'workOrders', id, 'updates'),
      snap => {
        const ups = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setUpdates(ups.sort((a,b)=>b.createdAt?.seconds - a.createdAt?.seconds));
      }
    );
    return () => unsub();
  }, [id]);

  // 5) When you pick a vendor in the dropdown, load its info
  useEffect(() => {
    const v = vendors.find(v=>v.id===assignedVendor);
    setVendorInfo(v||null);
  }, [assignedVendor, vendors]);

// whenever you pick a PM, load their info into pmInfo
useEffect(() => {
  const p = vaUsers.find(u => u.id === assignedVa);
  setVaInfo(p || null);
}, [assignedVa, vaUsers]);

  // 6) logUpdate helper
  const logUpdate = async (msg, system=false) => {
    await addDoc(collection(db,'workOrders',id,'updates'), {
      message: msg,
      sender: system ? 'System' : 'Admin',
      system,
      collaborators,
      createdAt: serverTimestamp()
    });
  };

  // 7) handleUpdate for status/priority/assignedVendor
  const handleUpdate = async (field, value) => {
        console.log('🔥 handleUpdate called:', { field, value, prevAssignedVendor });

    const ref = doc(db,'workOrders',id);
    await updateDoc(ref,{[field]:value});
    if (field==='status' && value!==prevStatus) {
      await logUpdate(`Status changed from ${prevStatus} to ${value}`, true);
      setPrevStatus(value);
    }
    if (field==='priority' && value!==prevPriority) {
      await logUpdate(`Priority changed from ${prevPriority} to ${value}`, true);
      setPrevPriority(value);
    }
    if (field === 'assignedVaId' && value !== prevAssignedVa) {
  // find the selected VA object
  const newVa = vaUsers.find(v => v.id === value);
  const vaName = newVa?.name || value;

  // log the update
  await logUpdate(`Assigned VA changed to ${vaName}`, true);

  // bump your “previous” state
  setPrevAssignedVa(value);
}
if (field === 'assignedVendorId' && value !== prevAssignedVendor) {
  // find the selected vendor object
  const newVendor = vendors.find(v => v.id === value);
  const vendorName = newVendor?.name || value;

  console.log(`🔥 vendor changed! Generating PDF for vendor: ${vendorName}`);

  // include the name in your Firestore update too
  await logUpdate(`Assigned vendor changed to ${vendorName}`, true);

  await generateWorkOrderPdf();
  setPrevAssignedVendor(value);
}
  };

  // 8) handle file upload from PM “Add attachments” button
  const handleFileChange = async e => {
    const file = e.target.files[0];
    if (!file) return;
    const ts = Date.now();
    const path = `workOrders/${id}/pmAttachments/${ts}-${file.name}`;
    const ref = storageRef(storage, path);
    await uploadBytes(ref, file);
    const url = await getDownloadURL(ref);
    await addDoc(collection(db,'workOrders',id,'files'), {
      name: file.name,
      url,
      type: 'attachment',
      createdAt: serverTimestamp()
    });
    e.target.value = null;
  };
/**
 * Build a simple PDF from the current `workOrder` data,
 * upload it to Storage, and add it to the `files` sub-collection.
 */
// add this (or replace your old version) above handleUpdate:
const generateWorkOrderPdf = async () => {
    console.log('🔥 generateWorkOrderPdf start', { assignedVendor, prevAssignedVendor });

  if (!workOrder) return;

  const doc = new jsPDF({ unit: 'pt', format: 'letter' });
  const pageWidth = doc.internal.pageSize.getWidth();

  // 1) Draw the logo
  const img = new Image();
  img.src = logoPng;
  await new Promise(r => (img.onload = r));
  // x = 40pt, y = 40pt, width = 150pt, height auto
  const logoW = 150;
  const logoH = (img.height / img.width) * logoW;
  doc.addImage(img, 'PNG', 40, 40, logoW, logoH);

  // 2) Company address (you can customize)
  // 2) Company address, line by line
doc.setFontSize(10);
const addressLines = [
  '901 Home Solutions',
  '123 Main St.',
  'Memphis, TN 38103',
  '(901) 555-1234',
  'www.901homesolutions.com'
];
// start just below the logo
let ay = 40 + logoH + 20;
addressLines.forEach(line => {
  doc.text(line, 40, ay);
  ay += 12;
});

  // 3) “WORK ORDER” header block on the right
  doc.setFontSize(18);
  doc.setTextColor(100);
  doc.text('WORK ORDER', pageWidth - 150, 60);

  // Draw background rectangle for the WO# label
 // Auto-size the WO# box so the full ID fits
const margin = 40;
const idText = `WO # ${workOrder.id}`;
// compute text width + padding
const textWidth = doc.getTextWidth(idText) + 10;
const boxWidth  = Math.max(textWidth, 100);       // at least 100pt wide
const boxX      = pageWidth - margin - boxWidth;  // align to right margin

doc.setFillColor(0, 102, 153);
doc.rect(boxX, 80, boxWidth, 20, 'F');

doc.setTextColor(255);
doc.setFontSize(10);
// draw text right-aligned inside the box (5pt inset)
doc.text(idText, boxX + boxWidth - 5, 95, { align: 'right' });

doc.setTextColor(0);  // reset to black

  doc.setTextColor(0);             // reset to black
  doc.setFontSize(10);

  // 4) Job details table (you can expand these rows as needed)
const infoY = ay + 20;    // 20 pt gap after last address line
const requestedByName  = workOrder.tenantName    // or workOrder.requestedBy
                         || workOrder.requestedBy 
                         || '—';
  const customerUid      = workOrder.tenantUid     // or workOrder.customerId
                         || workOrder.customerId 
                         || '—';
  const departmentType   = workOrder.category  // or workOrder.department
                         || workOrder.department 
                         || '—';

  // 4) Job details table, using those variables
  autoTable(doc, {
    startY: infoY,
    theme: 'plain',
    styles: { fontSize: 9, textColor: 50 },
    body: [
      ['Requested by:', requestedByName, 'Date:', new Date().toLocaleDateString()],
      ['Customer ID:',   customerUid,   'Department:', departmentType           ]
    ],
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 80 },
      2: { fontStyle: 'bold', cellWidth: 50 }
    },
    tableLineColor: 200,
    tableLineWidth: 0.5
  });

   // margins for the table
 const mLeft = 40, mRight = 40;
 // widths for the fixed-width columns
 const w = {
   qty: 30,
   title: 80,
   taxed: 30,
   unit: 60,
   total: 60
 };
 // calculate remaining width for DESCRIPTION
 const availableW = pageWidth - mLeft - mRight;
 const descW = availableW - (w.qty + w.title + w.taxed + w.unit + w.total);

  // 5) Line-item table (Parts & Labor)
const itemsY = doc.lastAutoTable.finalY + 20;
 autoTable(doc, {
   startY: itemsY,
   margin: { left: mLeft, right: mRight },
   head: [[ 'QTY', 'TITLE', 'DESCRIPTION', 'TAXED', 'UNIT PRICE', 'TOTAL' ]],
   body: [[
     '1',
     workOrder.subject,
     workOrder.description || '—',
     '-',
     '-',
     '-'
   ]],
   styles: { fontSize: 9 },
   headStyles: { fillColor: [0,102,153], textColor: 255 },
   columnStyles: {
     0: { cellWidth: w.qty },
     1: { cellWidth: w.title },
     2: { cellWidth: descW },
     3: { halign: 'center', cellWidth: w.taxed },
     4: { halign: 'right', cellWidth: w.unit },
     5: { halign: 'right', cellWidth: w.total }
   }
 });
// 6a) If there’s an image on the workOrder, embed it below the signature
const sigY = doc.lastAutoTable.finalY + 40;
doc.setDrawColor(150);
doc.line(40, sigY, pageWidth - 40, sigY);
doc.setFontSize(10);
doc.text('Signature', 40, sigY + 12);

// 6a) Now that sigY exists, embed the first work-order image (if any)
const imgUrl =
  workOrder.imageUrl ||
  workOrder.ppUrl ||
  (Array.isArray(workOrder.imageUrls) && workOrder.imageUrls[0]?.url);

if (imgUrl) {
  try {
    const img2 = new Image();
    img2.src = imgUrl;
    await new Promise(r => (img2.onload = r));

const desiredW = 200;
const imgW = Math.min(desiredW, pageWidth - 80); // 40pt margins on each side
const imgH = (img2.height / img2.width) * imgW;
doc.addImage(img2, 'PNG', 40, sigY + 20, imgW, imgH);
  } catch (err) {
    console.warn('Could not load work order image', err);
  }
}

  // 7) Export & upload (same as before)
  const pdfBlob = doc.output('blob');
  const ts = Date.now();
  const path = `workOrders/${id}/pmAttachments/${ts}-work-order.pdf`;
  const storageReference = storageRef(storage, path);
  await uploadBytes(storageReference, pdfBlob);
  const url = await getDownloadURL(storageReference);
  await addDoc(collection(db, 'workOrders', id, 'files'), {
    name: 'Work order.pdf',
    url,
    type: 'file',
    createdAt: serverTimestamp()
  });
  console.log('🔥 PDF generated and uploaded');
};

  if (!workOrder) return <Typography>Loading…</Typography>;
  const sectionStyles = { p:3, mb:3, borderRadius:3, boxShadow:3 };

  return (
    <Box sx={{ display:'flex', gap:3, p:3 }}>
      <Box flex={3}>
        {/* --- Header & Controls --- */}
        <Paper sx={{ ...sectionStyles, mb:3 }}>
          <Typography variant="h5" gutterBottom>
            {workOrder.subject}
          </Typography>
          <Typography variant="body1" gutterBottom>
            {workOrder.description}
          </Typography>
          <Grid container spacing={2} alignItems="center" sx={{ mt:1 }}>
            {/* Status */}
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={status}
                  label="Status"
                  onChange={e=>setStatus(e.target.value)}
                >
                  {['New','In progress','Completed','Deferred','Closed']
                    .map(s=><MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            {/* Priority */}
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={priority}
                  label="Priority"
                  onChange={e=>setPriority(e.target.value)}
                >
                  {['High','Medium','Low']
                    .map(p=><MenuItem key={p} value={p}>{p}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            {/* Vendor */}
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth>
                <InputLabel>Vendor</InputLabel>
                <Select
                  value={assignedVendor}
                  label="Vendor"
                  onChange={e=>setAssignedVendor(e.target.value)}
                >
                  {vendors.map(v=>(
                    <MenuItem key={v.id} value={v.id}>
                      {v.name||v.email}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
  <FormControl fullWidth>
    <InputLabel>VA</InputLabel>
    <Select
      value={assignedVa}
      label="VA"
      onChange={e => setAssignedVa(e.target.value)}
    >
      {vaUsers.map(pm => (
        <MenuItem key={pm.id} value={pm.id}>
          {pm.name || pm.email}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
</Grid>
            {/* Save */}
            <Grid item xs={12} sm={3}>
              <Box sx={{ display:'flex', gap:1 }}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={async()=>{
                    await handleUpdate('status', status);
                    await handleUpdate('priority', priority);
                    await handleUpdate('assignedVendorId', assignedVendor);
                     await handleUpdate('assignedVaId', assignedVa);  // <-- corrected here
                  }}
                >
                  Update Task
                </Button>
                <IconButton color="primary"><Print/></IconButton>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* --- Custom Fields (unchanged) --- */}
        <Paper sx={sectionStyles}>
          <Typography variant="h6">Custom fields</Typography>
          <Typography variant="body2" sx={{ mt:1 }}>
            No custom fields added.
          </Typography>
          <Button sx={{ mt:2 }}>+ Add custom field</Button>
        </Paper>

        {/* --- Files Panel --- */}
<Paper sx={sectionStyles}>
  <Typography variant="h6">Files</Typography>

  
<List>
  {attachments.map((file, idx) => {
    // detect images by your existing type flag or by file name
    const isImage = file.type === 'image'
      || /\.(png|jpe?g|gif|webp)$/i.test(file.name);

    return (
      <ListItem
        key={idx}
        button
        onClick={() => {
          if (!isImage) {
            // download PDFs and any “true” file types
            const link = document.createElement('a');
            link.href = file.url;
            link.download = file.name;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          } else {
            // images open in modal
            setExpandedImage(file.url);
          }
        }}
      >
        {isImage ? (
          <Avatar
            variant="square"
            src={file.url}
            sx={{ width: 48, height: 48, mr: 2 }}
          />
        ) : (
          <PictureAsPdf color="error" sx={{ fontSize: 48, mr: 2 }} />
        )}
        <ListItemText primary={file.name} />
      </ListItem>
    );
  })}
</List>

<Dialog
  open={Boolean(expandedImage)}
  onClose={() => setExpandedImage(null)}
>
  {expandedImage && (
    <Box p={2}>
      <img
        src={expandedImage}
        alt="expanded"
        style={{ maxWidth: '100%', maxHeight: '80vh' }}
      />
    </Box>
  )}
</Dialog>

  {/* only image dialog now */}
  <Dialog open={Boolean(expandedImage)} onClose={() => setExpandedImage(null)}>
    {expandedImage && (
      <Box sx={{ p: 2 }}>
        <img
          src={expandedImage}
          alt="expanded"
          style={{ maxWidth: '100%', maxHeight: '80vh' }}
        />
      </Box>
    )}
  </Dialog>

  {/* attachment input + button (unchanged) */}
  <input
    type="file"
    accept="image/*"
    ref={fileInputRef}
    style={{ display: 'none' }}
    onChange={handleFileChange}
  />
  <Button sx={{ mt: 2 }} onClick={() => fileInputRef.current.click()}>
    + Add attachments
  </Button>
</Paper>

        {/* --- Parts & Labor (unchanged) --- */}
        <Paper sx={sectionStyles}>
          <Typography variant="h6">Parts and labor</Typography>
          <Typography variant="body2" sx={{ mt:1 }}>
            No entries yet.
          </Typography>
        </Paper>

        {/* --- Updates Feed --- */}
        <Paper sx={sectionStyles}>
          <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <Typography variant="h6">Updates</Typography>
            <IconButton onClick={()=>setModalOpen(true)}><Add/></IconButton>
          </Box>
          <Divider sx={{ my:2 }}/>
          {updates.map(u=>(
            <React.Fragment key={u.id}>
              <ListItem alignItems="flex-start">
                <ListItemIcon>
                  {u.system ? <SwapHoriz/> : <ChatBubbleOutline/>}
                </ListItemIcon>
                <Avatar sx={{ mr:2 }}>{u.sender?.[0]}</Avatar>
                <ListItemText
                  primary={`${u.sender||'Unknown'}${u.system?'':' sent a message'}`}
                  secondary={
                    <>
                      <Typography variant="caption" display="block" color="text.secondary">
                        {u.createdAt?.toDate().toLocaleString()||'Just now'}
                      </Typography>
                      <Typography variant="body2">{u.message}</Typography>
                      {u.collaborators?.length>0 && (
                        <Typography variant="caption" color="primary">
                          Tagged: {u.collaborators.map(cid=>allUsers.find(x=>x.id===cid)?.name).join(', ')}
                        </Typography>
                      )}
                    </>
                  }
                />
              </ListItem>
<Divider
  component="li"
  sx={{
    ml: 7,
    listStyleType: 'none',    // ← turn off the bullet
    // you can also tweak the border color here if you like
  }}
/>            </React.Fragment>
          ))}
        </Paper>
      </Box>

      {/* --- Right Column: Assignees & Contacts --- */}
      <Box flex={1}>
        <Paper sx={sectionStyles}>
  <Typography variant="h6">Assignees</Typography>
  <Divider sx={{ my:2 }}/>
  <ToggleButtonGroup
    value={assigneeType}
    exclusive
    onChange={(e, v) => v && setAssigneeType(v)}
    fullWidth
    sx={{ mb:2 }}
  >
    <ToggleButton value="staff">Staff</ToggleButton>
    <ToggleButton value="vendor">Vendors</ToggleButton>
  </ToggleButtonGroup>

  {assigneeType === 'staff' ? (
    vaInfo ? (
      <>
        <Typography variant="subtitle2">Assigned to</Typography>
        <Box sx={{ display:'flex', alignItems:'center', gap:1, mt:1 }}>
          <Avatar>{vaInfo.name?.[0]}</Avatar>
          <Box>
            <Typography>{vaInfo.name}</Typography>
            <Typography variant="body2" color="text.secondary">
              Staff member
            </Typography>
          </Box>
          <Button variant="text" size="small" sx={{ ml:'auto' }}>Edit</Button>
        </Box>
        <Button sx={{ mt:2 }} variant="outlined">Email staff</Button>
      </>
    ) : (
      <Typography variant="body2">No staff assigned</Typography>
    )
  ) : assigneeType === 'vendor' ? (
    vendorInfo ? (
      <>
        <Typography variant="subtitle2">Vendor</Typography>
        <Box sx={{ display:'flex', alignItems:'center', gap:1, mt:1 }}>
          <Avatar>{vendorInfo.name?.[0]}</Avatar>
          <Box>
            <Typography>{vendorInfo.name}</Typography>
            <Typography variant="body2" color="text.secondary">
              {vendorInfo.email}
            </Typography>
          </Box>
        </Box>
        <Button sx={{ mt:2 }} variant="outlined">Email vendors</Button>
      </>
    ) : (
      <Typography variant="body2">No vendor selected</Typography>
    )
  ) : null}
</Paper>

        {/* --- Contacts --- */}
<Paper sx={sectionStyles}>
  <Typography variant="h6">Contacts</Typography>
  <Divider sx={{ my: 2 }} />

  {/** build an array of everyone assigned: */}
  {[
    vaInfo && { ...vaInfo, label: 'Staff member' },
    vendorInfo && { id: vendorInfo.id, name: vendorInfo.name, email: vendorInfo.email, label: 'Vendor' }
  ]
    .filter(Boolean)
    .map(person => (
      <Box
        key={person.id}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 1
        }}
      >
        <Avatar>{person.name?.[0]}</Avatar>
        <Box>
          <Typography>{person.name}</Typography>
          <Typography variant="body2" color="text.secondary">
            {person.label}
          </Typography>
        </Box>
        <Button
          size="small"
          sx={{ ml: 'auto' }}
          variant="text"
          onClick={() => window.open(`mailto:${person.email}`)}
        >
          Email
        </Button>
      </Box>
    ))}

  {/** fallback if nobody is assigned */}
  {!(vaInfo || vendorInfo) && (
    <Typography variant="body2">No contacts assigned</Typography>
  )}
</Paper>
      </Box>

      {/* --- “New Update” Modal --- */}
      <Modal open={modalOpen} onClose={()=>setModalOpen(false)}>
        <Box
          sx={{
            position:'absolute', top:'50%', left:'50%',
            transform:'translate(-50%,-50%)', width:500,
            bgcolor:'background.paper', p:4, borderRadius:2, boxShadow:24
          }}
        >
          <Typography variant="h6" mb={2}>Update task</Typography>
          <FormControl fullWidth sx={{ mb:2 }}>
            <InputLabel>Collaborators</InputLabel>
            <Select
              multiple
              value={collaborators}
              onChange={e=>setCollaborators(e.target.value)}
              input={<OutlinedInput label="Collaborators"/>}
              renderValue={sel=>sel.map(id=>allUsers.find(u=>u.id===id)?.name).join(', ')}
            >
              {allUsers.map(u=>(
                <MenuItem key={u.id} value={u.id}>
                  <Checkbox checked={collaborators.includes(u.id)}/>
                  <ListItemText primary={u.name}/>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={newMessage}
            onChange={e=>setNewMessage(e.target.value)}
            placeholder="Enter update message…"
          />
          <Button
            fullWidth
            sx={{ mt:2 }}
            variant="contained"
            onClick={async()=>{
              if (!newMessage.trim()) return;
              await logUpdate(newMessage);
              setNewMessage('');
              setCollaborators([]);
              setModalOpen(false);
            }}
          >
            Submit
          </Button>
        </Box>
      </Modal>
    </Box>
  );
}